import React from 'react';
import i18next from 'i18next';
import { datadogRum } from '@datadog/browser-rum';

import logo from '../../assets/images/logos/logo-blue.png';
import './ErrorBoundary.css';

/**
 * Filet de sécurité contre la page blanche.
 *
 * Sans lui, une exception levée pendant un rendu démonte tout l'arbre React :
 * l'utilisateur se retrouve devant un écran vide, sans rien comprendre, et
 * abandonne. C'est ce qui est arrivé le 24/09 sur l'étape paiement.
 *
 * Deux contraintes, parce que l'instance racine est montée au-dessus du
 * ThemeProvider MUI et de I18nextProvider (cf. src/index.js) :
 *  - pas de composant MUI ni de `styled`, d'où le CSS importé à côté ;
 *  - pas de useTranslation, d'où l'appel direct au singleton i18next. Les
 *    libellés vivent dans public/locales/<lng>/common.json, section
 *    "error-boundary", comme le reste de l'application.
 *
 * `variant` existe parce que le message ne peut pas être le même partout :
 * une instance posée autour d'un tunnel de paiement peut affirmer qu'aucun
 * paiement n'a été confirmé, l'instance racine non — elle couvre aussi les
 * écrans d'après-paiement, où ce serait faux.
 */

/** Route publique : /home est une PrivateRoute qui renverrait un acheteur
 *  non connecté sur /login/classic (cf. AppRouter.jsx:97, PrivateRoute.js:87). */
const ACCUEIL_PUBLIC = 'https://studcorp.com/';

type Variant = 'default' | 'payment';

type Props = {
  children?: React.ReactNode;
  variant?: Variant;
};

type State = {
  hasError: boolean;
};

/**
 * i18next n'est pas monté en contexte React ici, mais c'est le même singleton
 * que celui passé à I18nextProvider, déjà initialisé par src/index.js. Le
 * `defaultValue` ne sert que si le namespace "common", chargé en HTTP, ne
 * l'est pas encore au moment du crash.
 */
const traduire = (cle: string, repli: string): string => {
  if (!i18next.isInitialized) {
    return repli;
  }
  return i18next.t(`error-boundary.${cle}`, { ns: 'common', defaultValue: repli });
};

const direction = (): 'rtl' | 'ltr' =>
  i18next.isInitialized && i18next.dir() === 'rtl' ? 'rtl' : 'ltr';

class ErrorBoundary extends React.Component<Props, State> {
  static defaultProps = {
    children: null,
    variant: 'default' as Variant,
  };

  /**
   * Le titre n'existe que dans le rendu d'erreur : cette ref se déclenche donc
   * exactement au moment où l'arbre est remplacé. L'élément qui avait le focus
   * (le bouton « Payer », typiquement) vient de disparaître, le focus est
   * retombé sur <body> et un lecteur d'écran n'annoncerait rien.
   */
  private focaliserTitre = (node: HTMLHeadingElement | null): void => {
    node?.focus();
  };

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Erreur non rattrapée :', error, info);
    datadogRum.addError(error, {
      source: 'error_boundary',
      variant: this.props.variant,
      componentStack: info?.componentStack,
    });
  }

  render(): React.ReactNode {
    const { children, variant } = this.props;
    const { hasError } = this.state;

    if (!hasError) {
      return children;
    }

    const cleTexte = variant === 'payment' ? 'description-payment' : 'description';
    const repliTexte =
      variant === 'payment'
        ? "Aucun paiement n'a été confirmé. Rechargez la page pour reprendre votre souscription."
        : 'Rechargez la page pour reprendre où vous en étiez.';

    return (
      <div className="sc-eb-ecran" dir={direction()} role="alert" aria-live="assertive">
        <div className="sc-eb-carte">
          <img className="sc-eb-logo" src={logo} alt="Studcorp" width={40} />

          <h1 className="sc-eb-titre" tabIndex={-1} ref={this.focaliserTitre}>
            {traduire('title', 'Une erreur est survenue')}
          </h1>

          <p className="sc-eb-texte">{traduire(cleTexte, repliTexte)}</p>

          <button
            type="button"
            className="sc-eb-bouton"
            onClick={() => window.location.reload()}
          >
            {traduire('reload', 'Recharger la page')}
          </button>

          <div>
            <a className="sc-eb-lien" href={ACCUEIL_PUBLIC}>
              {traduire('home', "Retour à l'accueil")}
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
